import { OrderedListOutlined } from '@ant-design/icons';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Button, Flex, Space, TableColumnsType, Typography,
} from 'antd';
import {
    useCallback, useMemo, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import {
    managerQueries, projectsMutations, projectsQueries,
} from '@/api';
import {
    ActionDeleteButton,
    ActionEditButton,
    ActionsButtonWrapper,
    ActionScriptToggleButton,
    ActionViewButton,
    ConfirmModal,
    CreateButton,
    DefaultTable,
    DeleteProjectModal,
    ProjectActiveBadge,
    ReorderModal,
    RoleGuard,
    ThesesBadge,
} from '@/components';
import {
    AppRoute, UserRole,
} from '@/constants';
import { useCustomParams } from '@/hooks';
import { useUserStore } from '@/stores';
import {
    DefaultOrderColumn, Project, SortDirection,
} from '@/types';
import {
    getActionsColumn,
    getUpdatedAtColumn,
} from '@/utils';
import { hasRole } from '@/utils/users.utils';

export const ProjectsPage = () => {
    const navigate = useNavigate();
    const { user } = useUserStore();
    const { currentPage, pageSize, search } = useCustomParams({ defaultPageSize: 50 });
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
    const [reorderModalOpen, setReorderModalOpen] = useState(false);
    const [toggleModalOpen, setToggleModalOpen] = useState(false);
    const [projectToToggle, setProjectToToggle] = useState<Project | null>(null);

    const isManager = useMemo(() => user?.role === UserRole.Manager, [user?.role]);

    const toggleMutationOptions = useMemo(
        () => projectToToggle
            ? projectsMutations.toggleActive(projectToToggle.id)
            : projectsMutations.toggleActive(''),
        [projectToToggle],
    );

    const { mutate: toggleProject, isPending: isToggling } = useMutation(toggleMutationOptions);

    const columns: TableColumnsType<Project> = useMemo(() => [
        {
            title: 'Назва',
            dataIndex: 'title',
            key: 'title',
            width: hasRole(user, [UserRole.Admin, UserRole.SuperAdmin]) ? 200 : 100,
            sorter: false,
            render: (title: string, record: Project) => (
                <Flex vertical gap={4}>
                    <Space size="small" align="center">
                        <Typography.Link
                            onClick={() => navigate(`${AppRoute.Projects}/${record.id}`)}
                            style={{
                                display: "flex",
                                gap: "10px"
                            }}
                        >
                            <span>
                                🔷
                            </span>
                            <span>{title}</span>
                        </Typography.Link>
                        <ProjectActiveBadge isActive={record.is_active !== false} />
                    </Space>
                    {/*<ThesesBadge content={record.description} />*/}
                </Flex>
            ),
        },
        {
            ...getUpdatedAtColumn(),
            sorter: false,
            sortDirections: undefined,
        },
        getActionsColumn({
            width: hasRole(user, [UserRole.Admin, UserRole.SuperAdmin]) ? undefined : 80,
            render: (_, record: Project) => {
                const handleDelete = () => {
                    setProjectToDelete(record);
                    setDeleteModalOpen(true);
                };

                const handleToggle = () => {
                    setProjectToToggle(record);
                    setToggleModalOpen(true);
                };

                return (
                    <ActionsButtonWrapper style={{ justifyContent: 'flex-end' }}>
                        <ActionViewButton
                            onClick={() => navigate(`${AppRoute.Projects}/${record.id}`)}
                            text="Переглянути"
                            to={`${AppRoute.Projects}/${record.id}`}
                        />
                        <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter]}>
                            <ActionEditButton
                                onClick={() => navigate(`${AppRoute.Projects}/${record.id}/edit`)}
                                to={`${AppRoute.Projects}/${record.id}/edit`}
                            />
                        </RoleGuard>

                        <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter]}>
                            <ActionScriptToggleButton
                                isActive={record.is_active !== false}
                                onClick={handleToggle}
                                disabled={isToggling}
                                styles={{
                                    marginLeft: "20px",
                                }}
                            />
                        </RoleGuard>
                        <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin]}>
                            <ActionDeleteButton onClick={handleDelete} />
                        </RoleGuard>
                    </ActionsButtonWrapper>
                );
            },
        }),
    ], [navigate, isToggling]);

    const projectsQuery = useMemo(() => {
        return isManager
            ? managerQueries.getManagerProjects({
                per_page: pageSize,
                current_page: currentPage,
                order_column: 'priority' as DefaultOrderColumn,
                order_direction: 'asc' as SortDirection,
                search: search,
            })
            : projectsQueries.getAllProjects({
                per_page: pageSize,
                current_page: currentPage,
                order_column: 'priority' as DefaultOrderColumn,
                order_direction: 'asc' as SortDirection,
                search: search,
            });
    }, [isManager, pageSize, currentPage, search]);

    const { data, isFetching } = useQuery(projectsQuery);
    const {
        data: reorderProjectsData,
        isFetching: isFetchingReorderProjects,
    } = useQuery({
        ...projectsQueries.getAllProjects({
            per_page: -1,
            current_page: 1,
            order_column: 'priority',
            order_direction: 'asc',
        }),
        enabled: reorderModalOpen && !isManager,
    });

    const deleteMutationOptions = useMemo(
        () => projectToDelete
            ? projectsMutations.delete(projectToDelete.id)
            : projectsMutations.delete(''),
        [projectToDelete],
    );

    const { mutate: deleteProject, isPending: isDeleting } = useMutation(deleteMutationOptions);
    const { mutate: reorderProjects, isPending: isReordering } = useMutation(projectsMutations.reorder());

    const handleDeleteConfirm = () => {
        if (projectToDelete) {
            deleteProject(undefined, {
                onSuccess: () => {
                    setDeleteModalOpen(false);
                    setProjectToDelete(null);
                },
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteModalOpen(false);
        setProjectToDelete(null);
    };

    const handleToggleConfirm = () => {
        if (projectToToggle) {
            toggleProject(undefined, {
                onSuccess: () => {
                    setToggleModalOpen(false);
                    setProjectToToggle(null);
                },
            });
        }
    };

    const handleToggleCancel = () => {
        setToggleModalOpen(false);
        setProjectToToggle(null);
    };

    const reorderItems = useMemo(() => {
        return (reorderProjectsData?.items || []).map((project) => ({
            id: project.id,
            title: project.title,
        }));
    }, [reorderProjectsData?.items]);

    const handleReorderSave = useCallback((ids: string[]) => {
        reorderProjects(
            { ids },
            {
                onSuccess: () => {
                    setReorderModalOpen(false);
                },
            },
        );
    }, [reorderProjects]);

    return (
        <>
            <Helmet>
                <title>Проекти</title>
            </Helmet>

            <DefaultTable
                title="Проекти"
                columns={columns}
                dataSource={data?.items || []}
                total={data?.total || 0}
                loading={isFetching}
                defaultPageSize={50}
                renderCreateButton={() => (
                    <Flex gap="small" wrap>
                        <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter]}>
                            <Button
                                icon={<OrderedListOutlined />}
                                onClick={() => setReorderModalOpen(true)}
                                disabled={isFetching}
                            >
                                Сортування
                            </Button>
                        </RoleGuard>
                        <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin]}>
                            <CreateButton
                                label="Створити проект"
                                disabled={isFetching}
                                to={AppRoute.ProjectCreate}
                            />
                        </RoleGuard>
                    </Flex>
                )}
            />

            <DeleteProjectModal
                open={deleteModalOpen}
                entityName={projectToDelete?.title}
                entityType="project"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                loading={isDeleting}
            />

            <ReorderModal
                open={reorderModalOpen}
                title="Сортування проектів"
                items={reorderItems}
                loading={isFetchingReorderProjects}
                saving={isReordering}
                onCancel={() => setReorderModalOpen(false)}
                onSave={handleReorderSave}
                emptyText="Немає проектів для сортування"
            />

            <ConfirmModal
                open={toggleModalOpen}
                confirmTitle={projectToToggle?.is_active !== false ? 'Відключення проекту' : 'Підключення проекту'}
                confirmMessage={
                    projectToToggle?.is_active !== false
                        ? `Ви впевнені, що хочете відключити проект "${projectToToggle?.title}"?`
                        : `Ви впевнені, що хочете підключити проект "${projectToToggle?.title}"?`
                }
                confirmOkText={projectToToggle?.is_active !== false ? 'Відключити' : 'Підключити'}
                onConfirm={handleToggleConfirm}
                onCancel={handleToggleCancel}
                loading={isToggling}
            />
        </>
    );
};
